import type { Injector, Type } from '@angular/core';
import type { ContextFamily, ContextTone } from '@quipex/shared/utils';
import type { Observable } from 'rxjs';
import type { ComponentProperties } from '../dialog';

export const FILE_PREVIEW_TYPE = {
    Pdf: 'pdf',
    Image: 'image',
    Doc: 'doc',
} as const;

export type FilePreviewType =
    (typeof FILE_PREVIEW_TYPE)[keyof typeof FILE_PREVIEW_TYPE];

export const FILE_PREVIEW_CONTENT_STATUS = {
    Idle: 'idle',
    Loading: 'loading',
    Success: 'success',
    Error: 'error',
} as const;

export type FilePreviewContentStatus =
    (typeof FILE_PREVIEW_CONTENT_STATUS)[keyof typeof FILE_PREVIEW_CONTENT_STATUS];

export interface FilePreviewData {
    refreshPreviewUrl?: (() => Observable<string | null>) | undefined;
    previewUrl: string | null | undefined;
    fileName: string;
    initialPage?: number | undefined;
    searchTerm?: string | undefined;
    actions?: readonly FilePreviewAction[] | undefined;
    panels?: readonly FilePreviewPanelConfig[] | undefined;
    infoPanel?: FilePreviewInfoPanelConfig | undefined;
}

export interface FilePreviewPanelContent {
    panels?: readonly FilePreviewPanelConfig[] | undefined;
    infoPanel?: FilePreviewInfoPanelConfig | undefined;
}

export type FilePreviewPanelContentFactory = (
    injector: Injector
) => FilePreviewPanelContent;

export interface FilePreviewDialogData extends FilePreviewData {
    ariaLabel?: string | undefined;
    panelContentFactory?: FilePreviewPanelContentFactory | undefined;
}

export interface FilePreviewActionSelectContext {
    origin: EventTarget | null;
    event: Event;
}

export interface FilePreviewAction {
    id: string;
    label: string;
    icon: string;
    ariaLabel?: string | undefined;
    tooltip?: string | undefined;
    disabled?: boolean | undefined;
    ariaDisabled?: boolean | undefined;
    requiresViewerReady?: boolean | undefined;
    context?: ContextFamily | undefined;
    tone?: ContextTone | undefined;
    hasPopup?: 'dialog' | 'menu' | 'true' | undefined;
    expanded?: boolean | undefined;
    onSelect: (context?: FilePreviewActionSelectContext) => void;
}

export interface FilePreviewInfoPanelConfig<TComponent = unknown> {
    component: Type<TComponent>;
    scrollable?: boolean | undefined;
    componentInputs?: Partial<ComponentProperties<TComponent>> | undefined;
    title?: string | undefined;
    ariaLabel?: string | undefined;
}

export const FILE_PREVIEW_PANEL_SIDE = {
    Left: 'left',
    Right: 'right',
} as const;

export type FilePreviewPanelSide =
    (typeof FILE_PREVIEW_PANEL_SIDE)[keyof typeof FILE_PREVIEW_PANEL_SIDE];

export interface FilePreviewPanelConfig<TComponent = unknown> {
    id: string;
    side: FilePreviewPanelSide;
    label: string;
    icon: string;
    className?: string | undefined;
    /** Optional separate toolbar group for right-side panel controls. */
    controlGroup?: string | undefined;
    component: Type<TComponent>;
    scrollable?: boolean | undefined;
    componentInputs?: Partial<ComponentProperties<TComponent>> | undefined;
    ariaLabel: string;
    disabled?: boolean | undefined;
    initiallyActive?: boolean | undefined;
    retainOnSourceChange?: boolean | undefined;
    singlePaneBelow?: 'desktop' | undefined;
    requiresViewerReady?: boolean | undefined;
}

export type PreviewZoom =
    | 'auto'
    | 'page-width'
    | 'page-fit'
    | 'page-actual'
    | number;

export const PDF_PREVIEW_MIN_ZOOM = 25;
export const PDF_PREVIEW_MAX_ZOOM = 2000;

export function clampPdfPreviewZoom(zoom: number): number {
    return Math.min(Math.max(zoom, PDF_PREVIEW_MIN_ZOOM), PDF_PREVIEW_MAX_ZOOM);
}

export type PreviewRotation = 0 | 90 | 180 | 270;

export const PDF_SIDEBAR_TAB = {
    Thumbnails: 'thumbnails',
    Outline: 'outline',
    Layers: 'layers',
} as const;

export type PdfSidebarTab =
    (typeof PDF_SIDEBAR_TAB)[keyof typeof PDF_SIDEBAR_TAB];

export interface PdfViewerControlState {
    currentPage: number;
    pageCount: number | null;
    zoom: string | number;
    sidebarVisible: boolean;
    findbarVisible: boolean;
    canSearch: boolean;
}

export interface PdfThumbnailItem {
    page: number;
    imageUrl: string | null;
}

export interface PdfOutlineItem {
    id: string;
    title: string;
    dest: string | unknown[] | null;
    url: string | null;
    newWindow: boolean | undefined;
    items: PdfOutlineItem[];
}

export interface PdfLayerItem {
    layerId: string;
    name: string;
    visible: boolean;
}
